import React, { useEffect } from 'react';
import AIChat from './components/AIChat';
import { Routes, Route, useLocation } from 'react-router-dom';
import { ShieldOff } from 'lucide-react';
import Navbar from './components/Navbar';
import Footer from './components/footer';
import Home from './pages/Home';
import Verify from './pages/Verify';
import ATSChecker from './pages/ATSChecker';
import JobEmailBuilder from './pages/JobEmailBuilder';
import ResumeBuilder from './pages/ResumeBuilder';
import Compiler from './pages/Compiler';
import QuizRegistration from './pages/QuizRegistration';
import QuizResult from './pages/QuizResult';
import ReferEarn from './pages/ReferEarn';
import Reviews from './pages/Reviews';
import Contact from './pages/Contact';
import Practice from './pages/Practice';
import PracticeDetails from './pages/PracticeDetails';
import Tutorials from './pages/Tutorials';
import SubTutorial from './pages/SubTutorial';
import LoginSuccess from './pages/LoginSuccess';
import Services from './pages/Services';
import ApplyCourses from './pages/ApplyCourses';
import './index.css';
import Register from './components/Register';
import Login from './components/Login';
import Dashboards from './pages/Dashboards';
import Skilcourse from './pages/Skilcourse';
import Mycertificates from './pages/Mycertificates';
import Verifiedcertificates from './pages/Verifiedcertificates';
import Webdevquiz from './quizes/Webdevquiz';
import CourseQuiz from './quizes/CourseQuiz';
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiesPolicy from './pages/CookiesPolicy';
import Interviewprep from './pages/Interviewprep';
import ResumeScanner from './pages/ResumeScanner';
import ChangePassword from './components/ChangePassword';

// ScrollToTop component to handle scrolling to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Instant scroll to top on route change
    if (pathname === '/verify') return;
    
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });
    
    // Also scroll any scrollable containers
    const scrollableElements = document.querySelectorAll('.overflow-auto, .overflow-y-auto, .scrollable');
    scrollableElements.forEach(element => {
      element.scrollTop = 0;
    });
  }, [pathname]);

  return null;
};

const App = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Helper function to check if current route is verify
    const isVerifyRoute = () => {
      return pathname === '/verify';
    };

    // 1. Disable Text Selection (Copy/Paste)
    const disableTextSelection = (e) => {
      if (isVerifyRoute()) return true; // Allow on verify route
      e.preventDefault();
      return false;
    };

    // Disable copy, cut, paste
    const disableCopyCut = (e) => {
      if (isVerifyRoute()) return true; // Allow on verify route
      e.preventDefault();
      if (!isVerifyRoute()) {
        showBlackWarning(' Text copy is disabled on this site!', 'copy');
      }
      return false;
    };

    const handlePaste = (e) => {
      if (isVerifyRoute()) return true; // Allow on verify route
      e.preventDefault();
      showBlackWarning(' Paste is disabled on this site!', 'paste');
      return false;
    };

    // Apply to entire document
    document.addEventListener('selectstart', disableTextSelection);
    document.addEventListener('dragstart', disableTextSelection);
    document.addEventListener('contextmenu', (e) => {
      if (isVerifyRoute()) return true; // Allow right-click on verify route
      e.preventDefault();
      showBlackWarning(' Protected Content!', 'rightclick');
      return false;
    });
    
    // Disable copy, cut, paste
    document.addEventListener('copy', disableCopyCut);
    document.addEventListener('cut', disableCopyCut);
    document.addEventListener('paste', handlePaste);

    // 3. Disable Keyboard Shortcuts with Black Warning (except on verify route)
    const handleKeyDown = (e) => {
      if (isVerifyRoute()) return true; // Allow all keyboard shortcuts on verify route
      
      // F12 key
      if (e.keyCode === 123) {
        e.preventDefault();
        showBlackWarning(' Developer tools are disabled!', 'keyboard');
      }
      // Ctrl+Shift+I (Inspect), Ctrl+Shift+J (Console), Ctrl+Shift+C (Element selector)
      if (e.ctrlKey && e.shiftKey && (e.keyCode === 73 || e.keyCode === 74 || e.keyCode === 67)) {
        e.preventDefault();
        showBlackWarning(' Inspect element is disabled!', 'keyboard');
      }
      // Ctrl+U (View Source)
      if (e.ctrlKey && e.keyCode === 85) {
        e.preventDefault();
        showBlackWarning(' View source is disabled!', 'keyboard');
      }
      // Ctrl+S (Save)
      if (e.ctrlKey && e.keyCode === 83) {
        e.preventDefault();
        showBlackWarning(' Save is disabled!', 'keyboard');
      }
      // Ctrl+P (Print)
      if (e.ctrlKey && e.keyCode === 80) {
        e.preventDefault();
        showBlackWarning(' Print is disabled!', 'keyboard');
      }
      // Ctrl+C (Copy)
      if (e.ctrlKey && e.keyCode === 67) {
        e.preventDefault();
        showBlackWarning(' Copy is disabled!', 'copy');
      }
      // Ctrl+X (Cut)
      if (e.ctrlKey && e.keyCode === 88) {
        e.preventDefault();
        showBlackWarning(' Cut is disabled!', 'cut');
      }
      // Ctrl+V (Paste)
      if (e.ctrlKey && e.keyCode === 86) {
        e.preventDefault();
        showBlackWarning(' Paste is disabled!', 'paste');
      }
      // Ctrl+A (Select All)
      if (e.ctrlKey && e.keyCode === 65) {
        e.preventDefault();
        showBlackWarning(' Select All is disabled!', 'select');
      }
    };

    // Black warning function at top position
    const showBlackWarning = (message, type = 'default') => {
      // Remove any existing warning
      const existingWarning = document.getElementById('black-warning');
      if (existingWarning) {
        existingWarning.remove();
      }

      // Create warning element
      const warningMessage = document.createElement('div');
      warningMessage.id = 'black-warning';
      
      // ShieldOff SVG icon string
      const shieldOffIconString = `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>`;
      
      let iconHtml = '';
      if (type === 'copy') iconHtml = shieldOffIconString;
      if (type === 'rightclick') iconHtml = shieldOffIconString;
      if (type === 'keyboard') iconHtml = shieldOffIconString;
      if (type === 'paste') iconHtml = shieldOffIconString;
      if (type === 'cut') iconHtml = shieldOffIconString;
      if (type === 'select') iconHtml = shieldOffIconString;
      if (type === 'default') iconHtml = shieldOffIconString;
      
      warningMessage.innerHTML = `
        <div style="
          position: fixed;
          top: 30px;
          left: 50%;
          transform: translateX(-50%);
          background: #1a1a1a;
          color: white;
          padding: 14px 28px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 500;
          z-index: 10000;
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.3);
          display: flex;
          align-items: center;
          gap: 12px;
          animation: slideDown 0.3s ease-out;
          font-family: system-ui, -apple-system, sans-serif;
          border: 1px solid rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(8px);
          background: rgba(0, 0, 0, 0.92);
          letter-spacing: 0.3px;
          max-width: 90%;
          white-space: nowrap;
        ">
          <span style="font-size: 20px; display: flex; align-items: center;">${iconHtml}</span>
          <span style="flex: 1;">${message}</span>
          <button onclick="this.closest('#black-warning').remove()" style="
            background: rgba(255,255,255,0.15);
            border: none;
            color: white;
            font-size: 18px;
            cursor: pointer;
            margin-left: 12px;
            width: 26px;
            height: 26px;
            border-radius: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s;
          " onmouseover="this.style.background='rgba(255,255,255,0.25)'" onmouseout="this.style.background='rgba(255,255,255,0.15)'">×</button>
        </div>
        <style>
          @keyframes slideDown {
            0% {
              opacity: 0;
              transform: translateX(-50%) translateY(-20px);
            }
            100% {
              opacity: 1;
              transform: translateX(-50%) translateY(0);
            }
          }
          @keyframes slideUp {
            0% {
              opacity: 1;
              transform: translateX(-50%) translateY(0);
            }
            100% {
              opacity: 0;
              transform: translateX(-50%) translateY(-20px);
            }
          }
        </style>
      `;
      
      document.body.appendChild(warningMessage);
      
      // Auto remove after 2.5 seconds
      setTimeout(() => {
        const warning = document.getElementById('black-warning');
        if (warning) {
          const div = warning.querySelector('div');
          if (div) {
            div.style.animation = 'slideUp 0.3s ease-out';
          }
          setTimeout(() => warning.remove(), 300);
        }
      }, 2500);
    };

    // Add global CSS to prevent text selection (except on verify route)
    const style = document.createElement('style');
    style.id = 'global-text-selection-style';
    style.textContent = `
      * {
        -webkit-tap-highlight-color: transparent;
      }
    `;
    
    // Only add user-select restriction if not on verify route
    if (pathname !== '/verify') {
      style.textContent += `
        * {
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          user-select: none !important;
        }
        
        /* Allow selection only in input fields and textareas */
        input, textarea, [contenteditable="true"] {
          -webkit-user-select: text !important;
          -moz-user-select: text !important;
          -ms-user-select: text !important;
          user-select: text !important;
        }
      `;
    } else {
      // On verify route, allow text selection everywhere
      style.textContent += `
        * {
          -webkit-user-select: text !important;
          -moz-user-select: text !important;
          -ms-user-select: text !important;
          user-select: text !important;
        }
      `;
    }
    
    document.head.appendChild(style);

    // Add all event listeners
    window.addEventListener('contextmenu', (e) => {
      if (pathname === '/verify') return true;
      e.preventDefault();
      showBlackWarning(' Protected Content!', 'rightclick');
      return false;
    });
    window.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('selectstart', disableTextSelection);
      document.removeEventListener('dragstart', disableTextSelection);
      document.removeEventListener('copy', disableCopyCut);
      document.removeEventListener('cut', disableCopyCut);
      document.removeEventListener('paste', handlePaste);
      window.removeEventListener('keydown', handleKeyDown);
      const styleElement = document.getElementById('global-text-selection-style');
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, [pathname]); // Re-run effect when pathname changes

  return (
    <div className="font-sans text-slate-900 dark:text-slate-50 min-h-screen flex flex-col transition-colors duration-300">
      <Navbar />
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/apply" element={<ApplyCourses />} />
        <Route path="/verify" element={<Verify />} />
        <Route path="/ats-score" element={<ATSChecker />} />
        <Route path="/email-builder" element={<JobEmailBuilder />} />
        <Route path="/resume-builder" element={<ResumeBuilder />} />
        <Route path="/compiler" element={<Compiler />} />
        <Route path="/quiz-reg" element={<QuizRegistration />} />
        <Route path="/quiz-result" element={<QuizResult />} />
        <Route path="/refer" element={<ReferEarn />} />
        <Route path="/reviews" element={<Reviews />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/practice" element={<Practice />} />
        <Route path="/practice/:id" element={<PracticeDetails />} />
        <Route path="/tutorials" element={<Tutorials />} />
        <Route path="/tutorials/:id" element={<SubTutorial />} />
        <Route path="/login-success" element={<LoginSuccess />} />
        <Route path="/services" element={<Services />} />
        <Route path="/register" element={<Register />} />
        <Route path="/login" element={<Login />} />
        <Route path="/dashboards" element={<Dashboards />} />
        <Route path="/browse-courses" element={<Skilcourse />} />
        <Route path="/my-certificates" element={<Mycertificates />} />
        <Route path="/skill-certificate-verification" element={<Verifiedcertificates />} />
        <Route path="/quiz/:courseName" element={<CourseQuiz />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy/>} />
        <Route path="/cookies-policy" element={<CookiesPolicy/>} />
        <Route path="/interview-prep" element={<Interviewprep/>} />
        <Route path="/resume-scanner" element={<ResumeScanner/>} />
        <Route path="/refer-earn" element={<ReferEarn/>} />
        <Route path="/change-password" element={<ChangePassword/>} />
      </Routes>
      <Footer />
    </div>
  );
};

export default App;